import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STRUCTURE_LABEL } from "@/lib/content";
import { AdminTitle, UploadField } from "./ui";

const STRUCT = (withCommon) => [["ong", "ONG"], ["association", "Association"], ...(withCommon ? [["commun", "Commun"]] : [])];

export const CONFIGS = {
  letters: { title: "Lettres d'information", scope: "structure", fields: [
    { name: "title", label: "Titre de la lettre", required: true },
    { name: "structure", label: "Structure", type: "structure", common: true },
    { name: "excerpt", label: "Chapeau / résumé", type: "textarea", rows: 2 },
    { name: "content", label: "Contenu de la lettre", type: "textarea", rows: 14, required: true },
    { name: "cover_url", label: "Image (facultatif)", type: "upload", accept: "image/*" },
    { name: "published", label: "Publiée sur le site", type: "checkbox", default: true },
  ] },
  articles: { title: "Articles", scope: "category", fields: [
    { name: "title", label: "Titre", required: true },
    { name: "category", label: "Catégorie", type: "structure", common: true },
    { name: "excerpt", label: "Résumé", type: "textarea", rows: 2 },
    { name: "content", label: "Contenu", type: "textarea", rows: 10 },
    { name: "cover_url", label: "Image de couverture", type: "upload", accept: "image/*" },
    { name: "published", label: "Publié", type: "checkbox", default: true },
  ] },
  media: { title: "Galerie (photos & vidéos)", scope: "structure", fields: [
    { name: "title", label: "Titre / légende", required: true },
    { name: "type", label: "Type", type: "select", options: [["photo", "Photo"], ["video", "Vidéo (fichier MP4 ou lien YouTube)"]], default: "photo" },
    { name: "structure", label: "Structure", type: "structure", common: true },
    { name: "url", label: "Fichier ou URL", type: "upload", accept: "image/*,video/mp4,video/webm", required: true },
  ] },
  documents: { title: "Documents PDF", scope: "structure", fields: [
    { name: "title", label: "Titre", required: true },
    { name: "doc_type", label: "Type", type: "select", options: ["Statuts", "Règlement intérieur", "Bureau exécutif", "Autre"].map((o) => [o, o]), default: "Statuts" },
    { name: "structure", label: "Structure", type: "structure" },
    { name: "description", label: "Description" },
    { name: "url", label: "Fichier PDF", type: "upload", accept: "application/pdf", required: true },
  ] },
  members: { title: "Bureau exécutif", scope: "structure", fields: [
    { name: "name", label: "Nom", required: true },
    { name: "role", label: "Fonction", required: true },
    { name: "description", label: "Précisions (qualification, missions)", type: "textarea", rows: 2 },
    { name: "structure", label: "Structure", type: "structure" },
    { name: "photo_url", label: "Photo (non retouchée)", type: "upload", accept: "image/*" },
    { name: "order", label: "Ordre d'affichage", type: "number", default: 0 },
  ] },
};

function FormField({ f, value, set, structOpts }) {
  const tid = `content-field-${f.name}`;
  if (f.type === "textarea") return <textarea rows={f.rows} className="input" value={value ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
  if (f.type === "upload") return <UploadField value={value} onChange={set} accept={f.accept} testid={tid} />;
  if (f.type === "checkbox") return <input type="checkbox" className="h-5 w-5 accent-[var(--u)]" checked={!!value} onChange={(e) => set(e.target.checked)} data-testid={tid} />;
  if (f.type === "select" || f.type === "structure") {
    const opts = f.type === "structure" ? structOpts(f.common) : f.options;
    return <select className="input" value={value ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  }
  return <input type={f.type || "text"} required={f.required} className="input" value={value ?? ""} onChange={(e) => set(e.target.value)} data-testid={tid} />;
}

function Editor({ cfg, item, onClose, onSaved, coll, structOpts }) {
  const [v, setV] = useState(() => {
    const init = {};
    cfg.fields.forEach((f) => { init[f.name] = item?.[f.name] ?? f.default ?? (f.type === "structure" ? structOpts(f.common)[0][0] : ""); });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const miss = cfg.fields.find((f) => f.required && !v[f.name]);
    if (miss) return toast.error(`Champ obligatoire : ${miss.label}`);
    setSaving(true);
    try {
      if (item) await api.put(`/admin/content/${coll}/${item.id}`, v);
      else await api.post(`/admin/content/${coll}`, v);
      toast.success("Enregistré");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true">
      <form onSubmit={save} className="card my-8 w-full max-w-2xl space-y-4 p-6" data-testid="content-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold uppercase">{item ? "Modifier" : "Ajouter"}</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" data-testid="content-editor-close"><X className="h-5 w-5" /></button>
        </div>
        {cfg.fields.map((f) => (
          <div key={f.name}>
            <label className="label">{f.label}{f.required && " *"}</label>
            <FormField f={f} value={v[f.name]} set={(x) => setV((s) => ({ ...s, [f.name]: x }))} structOpts={structOpts} />
          </div>
        ))}
        <button className="btn-u" disabled={saving} data-testid="content-editor-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</button>
      </form>
    </div>
  );
}

export default function ContentManager({ coll }) {
  const cfg = CONFIGS[coll];
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get(`/admin/content/${coll}`).then((r) => setRows(r.data));
  useEffect(() => { setRows(null); load(); }, [coll]); // eslint-disable-line react-hooks/exhaustive-deps
  const structOpts = (common) => {
    const all = STRUCT(common);
    return user.role === "super_admin" ? all : all.filter(([v]) => v === user.role.replace("editor_", ""));
  };
  const del = async (it) => {
    if (!window.confirm(`Supprimer « ${it.title || it.name} » ?`)) return;
    try { await api.delete(`/admin/content/${coll}/${it.id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const thumb = (it) => it.cover_url || it.photo_url || (coll === "media" && it.type === "photo" ? it.url : null);
  return (
    <div data-testid={`admin-content-${coll}`}>
      <AdminTitle title={cfg.title}>
        <button className="btn-u !py-2" onClick={() => setEdit(null)} data-testid="content-add"><Plus className="h-4 w-4" /> Ajouter</button>
      </AdminTitle>
      <div className="card divide-y">
        {rows && !rows.length && <p className="p-6 text-neutral-500">Aucun élément.</p>}
        {(rows || []).map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-3" data-testid={`content-row-${it.id}`}>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded bg-u-soft">{thumb(it) && <img src={fileUrl(thumb(it))} alt="" className="h-full w-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{it.title || it.name}</p>
              <p className="truncate text-sm text-neutral-500">
                {STRUCTURE_LABEL[it[cfg.scope]]} · {it.role || it.doc_type || it.type || (it.published === false ? "Brouillon" : formatDate(it.created_at))}
              </p>
            </div>
            <button className="rounded-md p-2 hover:bg-neutral-100" onClick={() => setEdit(it)} aria-label="Modifier" data-testid={`content-edit-${it.id}`}><Pencil className="h-4 w-4" /></button>
            <button className="rounded-md p-2 text-asso hover:bg-asso/10" onClick={() => del(it)} aria-label="Supprimer" data-testid={`content-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {edit !== undefined && <Editor cfg={cfg} coll={coll} item={edit} structOpts={structOpts} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />}
    </div>
  );
}
